/* eslint-disable @typescript-eslint/no-explicit-any */
import { AdBreak, AdBreakMetadata, PlayerError, WarningEvent } from 'video-api';
import { logger } from 'logger';
import { datadogRum } from '@datadog/browser-rum';
import { AuthError } from '@top/auth-clientless-web-lite';
import { BillboardCategories, BillboardKeys } from 'components/Message/message-commander';

const LOG = logger.getLogger('dataDogMonitor');

export type CustomAttribute = 'webosVersion' | 'modelName' | 'apiLevel' | 'modelVersion';

export type ErrorContext = {
  billboard_code?: BillboardKeys;
  billboard_category?: string;
  error?: string | object;
  source?: string;
};

export type VideoErrorContext = {
  billboard_code: BillboardKeys;
  billboard_category?: string;
  error: PlayerErrorDetails;
};

export type PlayerErrorDetails = {
  categoryCode: string;
  code: string;
  message: string;
  source: string;
};

export type WarningContext = {
  warning: WarningEvent;
  source: string;
};

export type AuthErrorContext = {
  billboard_code: BillboardKeys;
  billboard_category?: string;
  error: AuthError;
  source: string;
};

export const Monitor = {
  // Sends error details to Datadog
  sendError(
    err: string | Error,
    errorContext?: ErrorContext | VideoErrorContext | WarningContext | AuthErrorContext | object,
  ): void {
    // Fill in any categories that are empty
    if (
      errorContext &&
      'billboard_code' in errorContext &&
      errorContext.billboard_code &&
      !('billboard_category' in errorContext)
    ) {
      errorContext.billboard_category = BillboardCategories[errorContext.billboard_code];
    }
    if (typeof err === 'string') {
      datadogRum.addError(new Error(err), { error: errorContext });
    } else {
      datadogRum.addError(err, { error: errorContext });
    }
  },
  // Sends action details to Datadog
  sendAction(eventName: string, metadata: object): void {
    datadogRum.addAction(eventName, { actionMetadata: metadata });
  },
  // Helper method to get player error info
  videoErrorToContext(billboardKey: BillboardKeys, error: PlayerError): VideoErrorContext {
    const errorContext: VideoErrorContext = {
      billboard_code: billboardKey,
      billboard_category: BillboardCategories[billboardKey],
      error: {
        categoryCode: error.error.categoryCode,
        code: error.error.code,
        message: error.error.toString(),
        source: 'top-sdk',
      },
    };
    return errorContext;
  },
  // Helper method to get player warning info
  videoWarningToContext(warning: WarningEvent): WarningContext {
    const warningContext: WarningContext = {
      warning: warning,
      source: 'top-sdk',
    };
    return warningContext;
  },
  // Helper method to get auth error info
  authErrorToContext(billboardKey: BillboardKeys, authError: AuthError): AuthErrorContext {
    const authErrorContext: AuthErrorContext = {
      billboard_code: billboardKey,
      billboard_category: BillboardCategories[billboardKey],
      error: authError,
      source: 'auth',
    };
    return authErrorContext;
  },
};

export default Monitor;
